import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const GOLD = '#d4af37';

interface ContactInfoContent {
  title: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  hours: string;
  additionalInfo: string;
}

const ContactInfoManager = () => {
  const [content, setContent] = useState<ContactInfoContent>({
    title: 'Contact Information',
    description: 'Get in touch with us through the following contact details.',
    email: 'info@muchukunnutemple.org',
    phone: '+91-XXXXXXXXXX',
    address: '123 Temple Street, City, State 123456, India',
    hours: 'Monday - Sunday: 5:00 AM - 9:00 PM',
    additionalInfo: 'For emergency temple services, please call during office hours.'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'contact_content', 'info'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as ContactInfoContent);
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'contact_content', 'info'), content);
      showSnackbar('Contact info saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field: keyof ContactInfoContent, value: string) => {
    setContent({ ...content, [field]: value });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Contact Information Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage temple contact information and details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          📋 Contact Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Title"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={content.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address"
              value={content.email}
              onChange={(e) => handleChange('email', e.target.value)}
              type="email"
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={content.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Temple Address"
              value={content.address}
              onChange={(e) => handleChange('address', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Operating Hours"
              value={content.hours}
              onChange={(e) => handleChange('hours', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Additional Information"
              value={content.additionalInfo}
              onChange={(e) => handleChange('additionalInfo', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactInfoManager;
